import {
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useGetTemplate } from '@/components/templates-v2/document-generation/hooks/useGetTemplate';
import { TemplateFieldEditor } from '@/components/templates-v2/document-generation/templates/TemplateInstantiator';
import { validateTemplateFields } from '@/components/treatment/v3/request/package-edit/utils/fieldValidation';
import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { Field, Label } from '@/components/ui/catalyst/fieldset';
import { Radio, RadioField, RadioGroup } from '@/components/ui/catalyst/radio';
import { Select } from '@/components/ui/catalyst/select';
import type { ReadTemplateSummary } from '@/services/api/generated/templatesV2Service';
import { useTemplateListQuery } from '@/services/api/generated/templatesV2Service';
import { useInvestigationRecordsRequestsGenerateFoiaLetterQuery } from '@/services/api/investigationRecordsRequestsService';
import { useGetMatterFlattenedContextQuery } from '@/services/api/matterFlattenedContextService';
import { useGetMatterQuery } from '@/services/api/mattersService';

interface GenerateFoiaLetterModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportId: string;
  matterId: string;
  departmentName: string;
}

export const GenerateFoiaLetterModal: React.FC<
  GenerateFoiaLetterModalProps
> = ({ isOpen, onClose, reportId, matterId, departmentName }) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [outputFormat, setOutputFormat] = useState<'docx' | 'pdf'>('docx');
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [showFields, setShowFields] = useState(false);

  // Fetch matter to get firm ID
  const { data: matter } = useGetMatterQuery(matterId, {
    skip: !matterId || !isOpen,
  });

  const firmId = matter?.firm?.id;

  // Fetch matter context with police report details
  const { data: matterContext } = useGetMatterFlattenedContextQuery(
    {
      matterId,
      policeReportId: reportId,
    },
    {
      skip: !matterId || !isOpen,
    },
  );

  // Fetch FOIA templates for the firm
  const {
    data: templatesData,
    isLoading: templatesLoading,
    error: templatesError,
    refetch: refetchTemplates,
  } = useTemplateListQuery(
    {
      firmId: firmId || '',
      category: 'FOIA_LETTER',
      scope: 'FIRM',
      status: 'ACTIVE',
    },
    {
      skip: !firmId || !isOpen,
    },
  );
  const templates = useMemo(
    () =>
      (templatesData?.results ?? []).filter(
        (t) => t.format !== 'EXTERNAL' && t.format !== 'SMS',
      ),
    [templatesData],
  );

  // State for triggering the query
  const [shouldGenerate, setShouldGenerate] = useState(false);
  const [generationParams, setGenerationParams] = useState<{
    policeReportId: string;
    templateId: string;
    format: string;
  } | null>(null);

  // RTK Query for generating FOIA letter (lazy query pattern)
  const { data: generatedBlob, isLoading: isGenerating } =
    useInvestigationRecordsRequestsGenerateFoiaLetterQuery(
      {
        matterId,
        policeReportId: reportId,
        templateId: generationParams?.templateId,
        format: generationParams?.format,
      },
      {
        skip: !shouldGenerate || !generationParams,
      },
    );

  // Fetch template details for validation
  const { template } = useGetTemplate(selectedTemplateId);

  // Auto-select first template when templates load
  useEffect(() => {
    if (templates.length > 0 && !selectedTemplateId) {
      setSelectedTemplateId(templates[0].id);
    }
  }, [templates, selectedTemplateId]);

  // Reset overrides when template changes
  useEffect(() => {
    setOverrides({});
    setShowFields(false);
  }, [selectedTemplateId]);

  const hasNoTemplates = !templatesLoading && templates.length === 0;

  const handleSetOverride = (fieldId: string, value: string) => {
    setOverrides((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  // Validate template fields
  const validation = useMemo(() => {
    if (!template || !matterContext) {
      return { isValid: true, missingFields: [], missingCount: 0 };
    }
    return validateTemplateFields(
      template.fields ?? [],
      matterContext as Record<string, string | undefined>,
      overrides,
    );
  }, [template, matterContext, overrides]);

  const hasMissingFields = validation.missingCount > 0;

  // Handle download when blob is ready
  useEffect(() => {
    if (generatedBlob && shouldGenerate) {
      try {
        // Determine file extension
        const extension = outputFormat === 'pdf' ? 'pdf' : 'docx';

        // Create download link
        const downloadUrl = window.URL.createObjectURL(generatedBlob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `FOIA_Letter_${departmentName.replace(/\s+/g, '_')}.${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        toast.success(`FOIA letter generated as ${extension.toUpperCase()}!`);
        onClose();
      } catch (error) {
        toast.error('Failed to download FOIA letter');
      } finally {
        setShouldGenerate(false);
        setGenerationParams(null);
      }
    }
  }, [generatedBlob, shouldGenerate, outputFormat, departmentName, onClose]);

  const handleGenerate = () => {
    if (!selectedTemplateId) {
      toast.error('Please select a template');
      return;
    }

    // Trigger the query
    setGenerationParams({
      policeReportId: reportId,
      templateId: selectedTemplateId,
      format: outputFormat,
    });
    setShouldGenerate(true);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} size="2xl">
      <DialogTitle>Generate FOIA Letter</DialogTitle>
      <DialogDescription>
        Generate a Freedom of Information Act letter for {departmentName}
      </DialogDescription>

      <DialogBody>
        {hasNoTemplates && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-amber-900">
                  No FOIA Template Found
                </h4>
                <p className="mt-1 text-sm text-amber-700">
                  Your firm doesn&apos;t have a FOIA letter template configured.
                  Please add a FOIA_LETTER template in your firm&apos;s template
                  settings to use this feature.
                </p>
                <div className="mt-3 flex items-center gap-3">
                  {firmId && (
                    <button
                      type="button"
                      onClick={() => {
                        window.open(`/firms/${firmId}/templates`, '_blank');
                      }}
                      className="text-sm font-medium text-amber-900 hover:text-amber-800 underline"
                    >
                      Go to Template Settings →
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => refetchTemplates()}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-900 hover:text-amber-800 px-2 py-1 rounded hover:bg-amber-100 transition-colors"
                  >
                    <ArrowPathIcon className="h-4 w-4" />
                    Refresh
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {templatesError && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-4">
            <p className="text-sm text-red-800">
              Failed to load templates. Please try again.
            </p>
          </div>
        )}
        {!hasNoTemplates && !templatesError && (
          <div className="space-y-6">
            {/* Department Info */}
            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <DocumentTextIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-blue-900">
                  {departmentName}
                </p>
                <p className="text-xs text-blue-700 mt-0.5">
                  Police Department
                </p>
              </div>
            </div>

            {/* Template Selection */}
            <Field>
              <Label>FOIA Template</Label>
              <Select
                name="template"
                value={selectedTemplateId}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setSelectedTemplateId(e.target.value)
                }
                disabled={templatesLoading || isGenerating}
              >
                {templatesLoading ? (
                  <option value="">Loading templates...</option>
                ) : (
                  templates.map((tmpl: ReadTemplateSummary) => (
                    <option key={tmpl.id} value={tmpl.id}>
                      {tmpl.display_name || tmpl.name}
                      {tmpl.is_default && ' (Default)'}
                    </option>
                  ))
                )}
              </Select>
            </Field>

            {/* Format Selection */}
            <RadioGroup
              value={outputFormat}
              onChange={(value: string) =>
                setOutputFormat(value as 'docx' | 'pdf')
              }
              disabled={isGenerating}
            >
              <Label>Output Format</Label>
              <div className="mt-2 flex gap-4">
                <RadioField>
                  <Radio value="docx" />
                  <Label>DOCX (Editable)</Label>
                </RadioField>
                <RadioField>
                  <Radio value="pdf" />
                  <Label>PDF (Final)</Label>
                </RadioField>
              </div>
            </RadioGroup>

            {/* Description */}
            <div className="text-sm text-gray-600 space-y-2">
              <p>
                This will generate a FOIA letter using the selected template
                with all relevant case information pre-filled.
              </p>
              <p>
                {outputFormat === 'docx'
                  ? 'Download as DOCX to review and edit before sending.'
                  : 'Download as PDF for a final, non-editable version.'}
              </p>
            </div>

            {/* Template Fields Section */}
            {selectedTemplateId && matterContext && (
              <div className="border-t pt-4">
                <button
                  type="button"
                  onClick={() => setShowFields(!showFields)}
                  className={`flex items-center justify-between w-full text-sm font-medium hover:text-gray-900 transition-colors ${
                    hasMissingFields
                      ? 'text-amber-700 hover:text-amber-800'
                      : 'text-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {showFields ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                    <span>
                      {showFields ? 'Hide' : 'Review & Edit'} Template Fields
                    </span>
                  </div>

                  {/* Status Indicator */}
                  <div className="flex items-center gap-1.5">
                    {hasMissingFields ? (
                      <>
                        <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
                        <span className="text-xs font-medium text-amber-700">
                          {validation.missingCount} missing
                        </span>
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="h-4 w-4 text-green-500" />
                        <span className="text-xs font-medium text-green-700">
                          Ready
                        </span>
                      </>
                    )}
                  </div>
                </button>

                {showFields && (
                  <div className="mt-4">
                    <TemplateFieldEditor
                      templateId={selectedTemplateId}
                      matterContext={
                        matterContext as Record<string, string | undefined>
                      }
                      overrides={overrides}
                      setOverride={handleSetOverride}
                      inline
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogBody>

      <DialogActions>
        <Button plain onClick={onClose} disabled={isGenerating}>
          {hasNoTemplates ? 'Close' : 'Cancel'}
        </Button>
        {!hasNoTemplates && !templatesError && (
          <Button
            onClick={handleGenerate}
            disabled={isGenerating || templatesLoading || !selectedTemplateId}
          >
            {isGenerating
              ? 'Generating...'
              : `Generate ${outputFormat.toUpperCase()}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
